/**
 * The Simple surface — `ob-2` (docs/ONBOARDING.md O4/O5).
 *
 * ## Why this file exists
 *
 * "Simple" was not simple. The Business-Owner nav carried **34 leaves** —
 * warehouse ops, fixed assets, loans, month-end accruals, audit finalization
 * and the partner program among them. That is the whole product with friendlier
 * labels, which is exactly the thing a shopkeeper cannot navigate.
 *
 * Simple is now **six fixed entries, in the order money moves**:
 *
 *     Home · Sales · Purchases · People & Products · Taxes · Reports
 *
 * plus at most **two** driven by the business nature (Point of Sale for a
 * counter, Stock for anyone holding goods). Hard cap: **eight**.
 *
 * ## The part that matters more than the short list
 *
 * Cutting a nav is easy; cutting it without losing anything is the actual job.
 * Every leaf that left the sidebar has a **home screen** it now lives inside —
 * `SIMPLE_HUB_LINKS` below is that map, rendered as a link strip under the page
 * title by `SimpleHubLinks`. `simpleNav.test.ts` walks the graph from the six
 * roots and asserts that every one of the 34 old leaves is still reachable. A
 * link that goes missing here fails the build, not the user.
 *
 * Two rules keep this honest (O5):
 *   - This is the **only** hand-kept list for the Simple surface. Whether a leaf
 *     is *applicable* stays the modules layer's job (`applyModuleGating` /
 *     `isNavItemVisibleByModule`) — never a second list here.
 *   - A link already showing as a sidebar row is dropped from the strip by the
 *     renderer, so nothing is ever offered twice.
 *
 * React-free (the `experienceLevel.ts` / `modules.ts` pattern) so it imports
 * anywhere, tests included; icons are lucide-react name strings.
 *
 * @since ob-2 (2026-07-28)
 */
import type { BusinessNature } from './businessNature';
import { NONPROFIT_ORG_TYPES } from './countryConfig';

/* ─── Types ───────────────────────────────────────────────────────────────── */

export interface SimpleNavEntry {
    /** Sidebar group id (`grp-*`). */
    groupId: string;
    /** The row label a non-accountant reads. */
    label: string;
    /** lucide-react icon name for the row. */
    iconName: string;
    /** The leaves under this row. One leaf ⇒ the row IS the link. */
    items: {
        id: string; label: string; iconName: string; href: string; hint?: string;
        /**
         * Only offer this leaf to these org types. Omitted means every org.
         *
         * Presentation only. Whether a leaf is APPLICABLE stays the modules
         * layer's job — `donations` belongs to the `ngo` module and
         * `applyModuleGating` would drop it for anyone else regardless. This
         * exists so the ORDER and the row LABEL can differ for a non-profit,
         * which gating cannot express.
         */
        orgTypes?: readonly string[];
    }[];
    /**
     * Natures this row appears for. Absent ⇒ always (one of the fixed six).
     * The modules layer still gets the final say on top of this.
     */
    natures?: BusinessNature[];
    /**
     * Rename the row for certain org types. A trust's money-in row is not
     * "Sales", and calling it that would bury the thing it does every day under
     * a word for something it does occasionally.
     */
    relabel?: { orgTypes: readonly string[]; label: string; iconName?: string };
}

export interface SimpleHubLink {
    /** Nav leaf id — so module gating and the "already in the sidebar" filter work. */
    id: string;
    label: string;
    iconName: string;
    href: string;
    /** One plain sentence on what lives there — Flow's tiles render it (wv-10). */
    hint?: string;
}

/* ─── The six (+ ≤2) ──────────────────────────────────────────────────────── */

/**
 * The Simple sidebar, top to bottom. Point of Sale sits with Sales and Stock
 * sits with Purchases, so the six fixed rows keep their money-moves order
 * whether or not the optional two appear.
 */
export const SIMPLE_NAV: readonly SimpleNavEntry[] = [
    {
        groupId: 'grp-dashboard',
        label: 'Home',
        iconName: 'LayoutDashboard',
        items: [{ id: 'dashboard', label: 'Home', iconName: 'LayoutDashboard', href: '/dashboard' }],
    },
    {
        // For a non-profit this row is "Money in" and Donations comes first,
        // because that is what a trust handles every day. Sales stays on it:
        // trusts invoice constantly (school fees, hospital billing, hall rent,
        // CSR project billing) and s.335 of the Income-tax Act 2025 counts
        // commercial gains as regular income, so hiding it would break them.
        //
        // Deliberately NOT a ninth row. The cap is eight and a trading-nature
        // org already uses all eight, so a new row would break the cap for the
        // exact org that needs this most.
        groupId: 'grp-sell',
        label: 'Sales',
        iconName: 'FileText',
        relabel: { orgTypes: NONPROFIT_ORG_TYPES, label: 'Money in', iconName: 'Heart' },
        items: [
            {
                id: 'donations', label: 'Donations', iconName: 'Heart', href: '/donations',
                hint: 'Record a donation and email the receipt',
                orgTypes: NONPROFIT_ORG_TYPES,
            },
            { id: 'sales', label: 'Sales', iconName: 'FileText', href: '/sales', hint: 'Bills you send, and who owes you' },
            // practice-door: a second leaf, so Simple keeps its row count; module gating drops it unless Karr Practice is on.
            { id: 'practice-matters', label: 'Karr Practice', iconName: 'Briefcase', href: '/practice/matters', hint: 'Matters, dates, time and bills' },
        ],
    },
    {
        groupId: 'grp-pos',
        label: 'Point of Sale',
        iconName: 'ShoppingCart',
        items: [{ id: 'pos', label: 'Point of Sale', iconName: 'ShoppingCart', href: '/pos', hint: 'Sell at the counter' }],
        natures: ['trading', 'mixed'],
    },
    {
        groupId: 'grp-buy',
        label: 'Purchases',
        iconName: 'Receipt',
        items: [{ id: 'expenses', label: 'Purchases', iconName: 'Receipt', href: '/expenses', hint: 'Bills you get, money you spend' }],
    },
    {
        groupId: 'grp-stock',
        label: 'Stock',
        iconName: 'Warehouse',
        items: [{ id: 'inventory', label: 'Stock', iconName: 'Warehouse', href: '/inventory', hint: "What's on the shelf" }],
        natures: ['trading', 'manufacturing', 'mixed'],
    },
    {
        groupId: 'grp-people',
        label: 'People & Products',
        iconName: 'Users',
        items: [{ id: 'contacts', label: 'People & Products', iconName: 'Users', href: '/contacts', hint: 'Customers, suppliers and your team' }],
    },
    {
        // Two leaves on purpose. An unregistered org has no GST cockpit, and a
        // single-leaf Taxes row would vanish with it, taking the deadlines page
        // down too. With both here the row survives either way — and when only
        // one leaf clears gating, the sidebar renders it as a plain "Taxes" row.
        groupId: 'grp-tax',
        label: 'Taxes',
        iconName: 'FileCheck',
        items: [
            { id: 'gst-filing', label: 'Tax Filing', iconName: 'FileCheck', href: '/gst-filing', hint: 'File GST on time' },
            { id: 'compliance', label: 'Rules & Deadlines', iconName: 'ShieldCheck', href: '/compliance', hint: 'Every rule that applies to you' },
        ],
    },
    {
        groupId: 'grp-review',
        label: 'Reports',
        iconName: 'BarChart3',
        items: [{ id: 'reports', label: 'Reports', iconName: 'BarChart3', href: '/reports', hint: 'Profit, cash and everything else' }],
    },
] as const;

/** How many rows Simple may ever show (O4). Asserted in `simpleNav.test.ts`. */
export const SIMPLE_NAV_MAX_ENTRIES = 8;

/**
 * The rows visible for a business nature and org type, before module gating.
 *
 * `orgType` is optional so every existing caller keeps working; omit it and the
 * result is exactly what it always was, because org-type-only leaves are then
 * filtered out rather than guessed at.
 */
export function simpleNavForNature(
    nature?: BusinessNature | string | null,
    orgType?: string | null,
): SimpleNavEntry[] {
    const n = String(nature ?? '');
    const t = String(orgType ?? '');
    return SIMPLE_NAV
        .filter(e => !e.natures || e.natures.includes(n as BusinessNature))
        .map((e) => {
            const items = e.items.filter(i => !i.orgTypes || i.orgTypes.includes(t));
            const rename = e.relabel && e.relabel.orgTypes.includes(t) ? e.relabel : null;
            return rename
                ? { ...e, items, label: rename.label, iconName: rename.iconName ?? e.iconName }
                : { ...e, items };
        })
        // A row whose every leaf was for someone else is not a row.
        .filter(e => e.items.length > 0);
}

/** Every leaf id the Simple sidebar can show, across all natures. */
export const SIMPLE_NAV_LEAF_IDS: ReadonlySet<string> = new Set(
    SIMPLE_NAV.flatMap(e => e.items.map(i => i.id)),
);

/* ─── Where everything else lives ─────────────────────────────────────────── */

/**
 * Screen → the surfaces that now live *inside* it. Rendered as a link strip
 * under the page title in Simple mode only (`SimpleHubLinks`).
 *
 * Each entry answers "if this isn't in the sidebar any more, where would a
 * person look for it?" — Payments Received under Sales, What to Pay under
 * Purchases, Month-end under Reports.
 */
export const SIMPLE_HUB_LINKS: Readonly<Record<string, readonly SimpleHubLink[]>> = {
    '/dashboard': [
        { id: 'my-businesses', label: 'My Businesses', iconName: 'Building2', href: '/my-businesses', hint: 'All your businesses, one screen' },
    ],
    '/sales': [
        { id: 'sales-orders', label: 'Orders', iconName: 'PackageCheck', href: '/sales-orders', hint: 'What customers have asked for' },
        { id: 'money-in', label: 'Payments Received', iconName: 'CreditCard', href: '/money-in', hint: 'Money that came in' },
        { id: 'credit-notes', label: 'Refunds', iconName: 'RotateCcw', href: '/credit-notes', hint: 'Money you gave back' },
        { id: 'payment-links', label: 'Collect Online', iconName: 'Send', href: '/payment-links', hint: 'Get paid with a link' },
        // The counter and the restaurant floor are ways of selling. They appear
        // here when they aren't already a row of their own.
        { id: 'pos', label: 'Point of Sale', iconName: 'ShoppingCart', href: '/pos', hint: 'Sell at the counter' },
        { id: 'restaurant', label: 'Restaurant', iconName: 'Utensils', href: '/restaurant', hint: 'Tables, orders and the floor' },
        { id: 'kds', label: 'Kitchen', iconName: 'ChefHat', href: '/kds', hint: 'What the kitchen cooks next' },
        { id: 'food-cost', label: 'Food Cost', iconName: 'Calculator', href: '/food-cost', hint: 'What a dish really costs' },
    ],
    '/pos': [
        { id: 'restaurant', label: 'Restaurant', iconName: 'Utensils', href: '/restaurant', hint: 'Tables, orders and the floor' },
        { id: 'kds', label: 'Kitchen', iconName: 'ChefHat', href: '/kds', hint: 'What the kitchen cooks next' },
        { id: 'food-cost', label: 'Food Cost', iconName: 'Calculator', href: '/food-cost', hint: 'What a dish really costs' },
    ],
    '/expenses': [
        { id: 'pay-week', label: 'What to Pay', iconName: 'Wallet', href: '/pay-week', hint: "What's due to be paid" },
        { id: 'money-out', label: 'Payments Made', iconName: 'Receipt', href: '/money-out', hint: 'Money you paid out' },
        { id: 'receipt-scanner', label: 'Scan Receipts', iconName: 'ScanLine', href: '/receipt-scanner', hint: 'Snap a bill, Karr records it' },
        { id: 'transfers', label: 'Move Money', iconName: 'ArrowLeftRight', href: '/transfers', hint: 'Between your own accounts' },
        { id: 'inventory', label: 'Stock', iconName: 'Warehouse', href: '/inventory', hint: "What's on the shelf" },
        // flow-1: rent, electricity, the audit fee. The Flow home's You owe
        // sheet lists it; this keeps it reachable from Purchases too.
        // fixcopy (the Flow audit of 30 Sep 2026): named for what it is. Rent and electricity
        // are fixed expenses (their own page, and the You owe sheet's own list), not these templates.
        { id: 'recurring-bills', label: 'Supplier bills that repeat', iconName: 'Repeat', href: '/recurring-bills', hint: 'A supplier\'s bill Karr raises by itself on a date' },
        // fixpage: the fixed expenses' own page (it left Settings), where a Simple owner meets it too.
        { id: 'fixed-expenses', label: 'Fixed Expenses', iconName: 'CalendarDays', href: '/fixed-expenses', hint: 'Rent, power: what you pay every month' },
    ],
    '/inventory': [
        { id: 'manufacturing', label: 'Manufacturing', iconName: 'Factory', href: '/manufacturing', hint: 'Materials into products' },
        { id: 'quality', label: 'Quality', iconName: 'BadgeCheck', href: '/quality', hint: 'Checks before goods ship' },
    ],
    '/contacts': [
        { id: 'items', label: 'Products & Services', iconName: 'Package', href: '/items', hint: 'What you sell, priced' },
        { id: 'collaboration', label: 'Access', iconName: 'UserCircle', href: '/collaboration', hint: 'Who can open this business' },
        // flow-1: "I paid my staff" needs Payroll reachable, and no Simple row
        // or strip carried it before.
        { id: 'payroll', label: 'Staff & Salaries', iconName: 'Briefcase', href: '/payroll', hint: 'Who works here, and their pay' },
    ],
    // fr-a1 — the Taxes row's second leaf is /compliance, and a Simple user had
    // no path from there to the notice inbox. A statutory window closing is the
    // last thing that should be reachable only in Standard mode.
    '/compliance': [
        { id: 'notices', label: 'Notices & Demands', iconName: 'Gavel', href: '/notices', hint: 'Letters from the department' },
        { id: 'books-health', label: 'Books Checkup', iconName: 'BadgeCheck', href: '/books-health', hint: 'Anything off in the books?' },
    ],
    '/reports': [
        { id: 'accruals', label: 'Month-end', iconName: 'CalendarDays', href: '/accruals', hint: 'Close the month properly' },
        { id: 'books-health', label: 'Books Checkup', iconName: 'BadgeCheck', href: '/books-health', hint: 'Anything off in the books?' },
        { id: 'fixed-assets', label: 'Things You Own', iconName: 'Building2', href: '/fixed-assets', hint: 'Machines, vehicles, property' },
        { id: 'loans', label: 'Loans & EMIs', iconName: 'Landmark', href: '/loans', hint: 'Loans and their EMIs' },
        { id: 'audit', label: 'Audit', iconName: 'ShieldCheck', href: '/audit', hint: 'Which audit applies, where it stands, what is next' },
        { id: 'ai-tools', label: 'AI Help', iconName: 'Bot', href: '/ai-tools', hint: 'Ask Karr to do it' },
        { id: 'marketplace', label: 'Marketplace', iconName: 'Store', href: '/marketplace', hint: 'Add-ons, prices, one button each' },
        { id: 'partner', label: 'Refer & Earn', iconName: 'Gift', href: '/partner', hint: 'Earn by referring Karr' },
        // flow-1: an owner matches their own statement, and brings in the
        // balances from before Karr, without switching to Books.
        { id: 'bank-statements', label: 'My Bank Statements', iconName: 'FileSpreadsheet', href: '/bank-statements', hint: 'Upload each statement; Karr checks it' },
        { id: 'bank-reconciliation', label: 'Match My Bank Statement', iconName: 'Link2', href: '/bank-reconciliation', hint: 'Tick off each line against your books' },
        { id: 'opening-balances', label: 'Old Balances', iconName: 'Wallet', href: '/opening-balances', hint: 'Where you stood before Karr' },
    ],
};

/** The links for a screen, or `[]` — safe to call for any route. */
export function simpleHubLinksFor(path: string): readonly SimpleHubLink[] {
    return SIMPLE_HUB_LINKS[path] ?? [];
}

/**
 * Every leaf the Simple surface can reach — sidebar rows plus everything the
 * hub strips carry. This is what `experienceLevel.ts` treats as "visible in
 * Simple", so there is exactly one list to keep (O5).
 */
export const SIMPLE_REACHABLE_LEAF_IDS: ReadonlySet<string> = new Set([
    ...SIMPLE_NAV_LEAF_IDS,
    ...Object.values(SIMPLE_HUB_LINKS).flatMap(links => links.map(l => l.id)),
]);
